import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, ArrowLeft, Mail, User, Phone, Building, Eye, EyeOff, BarChart3, Copy, Check, Sparkles, Shield, Zap, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

export default function AdvisorSignup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = form, 2 = success
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [errors, setErrors] = useState({});
  const [credentials, setCredentials] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'email') {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      } else {
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
      }
      toast.success(`${type === 'email' ? 'Email' : 'Password'} copied to clipboard!`);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone) newErrors.phone = 'Phone is required';
    else if (!/^[0-9]{10}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/advisor/signup`, formData);
      setCredentials(response.data);
      setStep(2);
      toast.success('Account created successfully! 🎉');
    } catch (error) {
      console.error('Signup error:', error);
      const errorMsg = error.response?.data?.detail || 'Error during signup. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  if (step === 2) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4 relative overflow-hidden">
        {/* Ambient glow - celebration colors */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-green-500/10 to-emerald-500/8 blur-3xl top-1/4 -left-32 animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute w-80 h-80 rounded-full bg-gradient-to-r from-orange-500/8 to-yellow-500/5 blur-3xl -top-20 right-0 animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />
          <div className="absolute w-64 h-64 rounded-full bg-gradient-to-r from-purple-500/5 to-pink-500/5 blur-3xl bottom-0 right-1/4 animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
        </div>

        <motion.div 
          className="max-w-2xl w-full relative z-10"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          <motion.div 
            className="bg-gray-800/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-10 shadow-2xl text-center"
            variants={fadeUp}
            transition={{ duration: 0.5 }}
          >
            {/* Animated checkmark */}
            <motion.div 
              className="w-20 h-20 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6, type: "spring", stiffness: 300 }}
              >
                <CheckCircle className="w-12 h-12 text-green-400" />
              </motion.div>
            </motion.div>
            
            <motion.h1 
              className="text-2xl md:text-3xl font-bold mb-2 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent"
              variants={fadeUp}
            >
              Account Created Successfully!
            </motion.h1>
            
            <motion.p className="text-base md:text-lg text-gray-300 mb-8" variants={fadeUp}>
              Welcome aboard, <span className="text-orange-400 font-semibold">{formData.name}</span>!
            </motion.p>
            
            {/* Credentials Card */}
            <motion.div 
              className="bg-gray-900/60 backdrop-blur-sm border border-white/5 rounded-xl p-6 mb-6"
              variants={fadeUp}
            >
              <div className="flex items-center gap-2 justify-center mb-5">
                <Shield className="w-5 h-5 text-orange-400" />
                <h2 className="text-lg font-semibold text-white">Your Login Credentials</h2>
              </div>
              <div className="space-y-3 text-left">
                {/* Email Row */}
                <div className="bg-gray-800/80 p-4 rounded-lg border border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-xs uppercase tracking-wider font-medium">Email</span>
                    <button
                      onClick={() => copyToClipboard(credentials?.email, 'email')}
                      className="flex items-center gap-1.5 px-3 py-1 bg-gray-700/80 hover:bg-gray-600 rounded-md text-gray-300 hover:text-white transition-colors text-xs"
                    >
                      {copiedEmail ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                    </button>
                  </div>
                  <span className="text-white font-mono text-sm break-all">{credentials?.email}</span>
                </div>

                {/* Password Row */}
                <div className="bg-gray-800/80 p-4 rounded-lg border border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-xs uppercase tracking-wider font-medium">Temporary Password</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="p-1 text-gray-400 hover:text-gray-300 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(credentials?.temp_password, 'password')}
                        className="flex items-center gap-1.5 px-3 py-1 bg-gray-700/80 hover:bg-gray-600 rounded-md text-gray-300 hover:text-white transition-colors text-xs"
                      >
                        {copiedPassword ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                  </div>
                  <span className="text-white font-mono text-sm">
                    {showPassword ? credentials?.temp_password : '••••••••••••'}
                  </span>
                </div>
              </div>
              <div className="mt-4 bg-orange-500/10 border border-orange-500/20 rounded-lg px-4 py-2.5 space-y-1.5">
                <p className="text-sm text-orange-300 flex items-center gap-2 justify-center">
                  <Shield className="w-4 h-4 flex-shrink-0" /> Save these credentials — you'll need them to login
                </p>
                <p className="text-xs text-gray-400 text-center">
                  We recommend updating your password in Account Settings after your first login
                </p>
              </div>
            </motion.div>

            {/* Trial Benefits */}
            <motion.div 
              className="bg-gradient-to-br from-orange-500/10 to-red-500/5 border border-orange-500/20 rounded-xl p-6 mb-8"
              variants={fadeUp}
            >
              <div className="flex items-center gap-2 justify-center mb-4">
                <Sparkles className="w-5 h-5 text-orange-400" />
                <h3 className="text-lg font-semibold text-orange-300">Your 7-Day Free Trial Includes</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Zap, text: "5 clients" },
                  { icon: Clock, text: "10 financial plans" },
                  { icon: Shield, text: "All features unlocked" },
                  { icon: CheckCircle, text: "No credit card" },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center gap-2 bg-gray-900/30 rounded-lg px-3 py-2.5"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + i * 0.1 }}
                  >
                    <item.icon className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div className="space-y-3" variants={fadeUp}>
              <Button
                onClick={() => navigate('/advisor/login')}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold h-12 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                Continue to Login
              </Button>
              <Link to="/advisors">
                <Button
                  variant="outline"
                  className="w-full h-12 border border-white/10 text-gray-300 hover:bg-gray-800/50 mt-2"
                >
                  Back to Home
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-orange-500/10 to-red-500/5 blur-3xl -top-32 -left-32 animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute w-72 h-72 rounded-full bg-gradient-to-r from-purple-500/5 to-pink-500/5 blur-3xl bottom-1/4 -right-20 animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
      </div>

      <motion.div 
        className="w-full max-w-2xl relative z-10"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
      >
        {/* Back to Landing */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4 }}>
          <Link 
            to="/advisors" 
            className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Advisor Home
          </Link>
        </motion.div>

        {/* Signup Card */}
        <motion.div 
          className="bg-gray-800/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl"
          variants={fadeUp}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <motion.div className="mb-8" variants={fadeUp} transition={{ duration: 0.4 }}>
            <div className="flex items-center gap-3 mb-6">
              <motion.div 
                className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg"
                whileHover={{ scale: 1.05, rotate: 3 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <span className="text-white font-bold text-xl">FA</span>
              </motion.div>
              <div>
                <h1 className="text-2xl font-bold text-white">FinAI</h1>
                <p className="text-sm text-gray-400">for Advisors</p>
              </div>
            </div>
            
            <div className="inline-flex items-center space-x-2 bg-orange-900/30 border border-orange-700 px-3 py-1.5 rounded-full mb-4">
              <BarChart3 className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs font-semibold text-orange-300">FOR FINANCIAL ADVISORS</span>
            </div>
            
            <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Start Your Free Trial
            </h2>
            <p className="text-gray-400">
              7 days free • No credit card required • Full access
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Field */}
            <div>
              <Label htmlFor="name" className="text-gray-300 font-medium flex items-center gap-2">
                <User className="w-4 h-4" />
                Full Name *
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                className={`mt-2 h-12 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.name ? 'border-red-500' : ''
                }`}
                placeholder="John Doe"
              />
              {errors.name && (
                <p className="text-red-400 text-sm mt-1.5 flex items-center gap-1">
                  <span>⚠</span> {errors.name}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <Label htmlFor="email" className="text-gray-300 font-medium flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`mt-2 h-12 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.email ? 'border-red-500' : ''
                }`}
                placeholder="advisor@company.com"
              />
              {errors.email && (
                <p className="text-red-400 text-sm mt-1.5 flex items-center gap-1">
                  <span>⚠</span> {errors.email}
                </p>
              )}
            </div>

            {/* Phone Field */}
            <div>
              <Label htmlFor="phone" className="text-gray-300 font-medium flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number *
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                className={`mt-2 h-12 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.phone ? 'border-red-500' : ''
                }`}
                placeholder="+91 98765 43210"
              />
              {errors.phone && (
                <p className="text-red-400 text-sm mt-1.5 flex items-center gap-1">
                  <span>⚠</span> {errors.phone}
                </p>
              )}
            </div>

            {/* Company Field (Optional) */}
            <div>
              <Label htmlFor="company" className="text-gray-300 font-medium flex items-center gap-2">
                <Building className="w-4 h-4" />
                Company Name (Optional)
              </Label>
              <Input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleInputChange}
                className="mt-2 h-12 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Your Company"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold h-12 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 mt-6"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating Account...
                </span>
              ) : (
                'Start Free Trial'
              )}
            </Button>
          </form>

          {/* Terms */}
          <p className="text-xs text-gray-500 text-center mt-6">
            By signing up, you agree to our <Link to="/terms" className="text-gray-400 hover:text-emerald-400 transition-colors">Terms of Service</Link> and <Link to="/privacy" className="text-gray-400 hover:text-emerald-400 transition-colors">Privacy Policy</Link>
          </p>

          {/* Login Link */}
          <motion.div className="mt-8 pt-6 border-t border-gray-700 text-center" variants={fadeUp}>
            <p className="text-gray-400">
              Already have an account?{' '}
              <Link to="/advisor/login" className="text-orange-400 hover:text-orange-300 font-semibold transition-colors">
                Sign in
              </Link>
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}